// AI Code Block Component — OFFSET
// Fond surface-3, coloration MONOCHROME (graisse et opacité, pas de teintes
// — l'erreur sémantique est la seule couleur), header mono chemin + « n lignes ».

import React, { memo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Copy, Check } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

export interface AICodeBlockProps {
    code: string;
    language?: string;
    filename?: string;
    showLineNumbers?: boolean;
    maxHeight?: string;
    className?: string;
}

// Language display names
const languageNames: Record<string, string> = {
    javascript: 'JavaScript',
    typescript: 'TypeScript',
    jsx: 'JSX',
    tsx: 'TSX',
    python: 'Python',
    html: 'HTML',
    css: 'CSS',
    json: 'JSON',
    bash: 'Bash',
    shell: 'Shell',
    sql: 'SQL',
    yaml: 'YAML',
    markdown: 'Markdown',
};

// Thème Prism monochrome OFFSET : l'encre module par graisse/opacité,
// aucune teinte décorative (le rouge sémantique marque les suppressions).
const offsetMonochrome: Record<string, React.CSSProperties> = {
    'code[class*="language-"]': {
        color: 'hsl(var(--foreground))',
        background: 'none',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        whiteSpace: 'pre',
        wordSpacing: 'normal',
        wordBreak: 'normal',
        tabSize: 2,
        hyphens: 'none',
    },
    'pre[class*="language-"]': {
        color: 'hsl(var(--foreground))',
        background: 'none',
        margin: 0,
        overflow: 'auto',
    },
    comment: { color: 'hsl(var(--faint))', fontStyle: 'italic' },
    prolog: { color: 'hsl(var(--faint))', fontStyle: 'italic' },
    doctype: { color: 'hsl(var(--faint))', fontStyle: 'italic' },
    cdata: { color: 'hsl(var(--faint))', fontStyle: 'italic' },
    punctuation: { color: 'hsl(var(--muted-foreground))' },
    operator: { color: 'hsl(var(--muted-foreground))' },
    keyword: { color: 'hsl(var(--foreground))', fontWeight: 600 },
    atrule: { color: 'hsl(var(--foreground))', fontWeight: 600 },
    important: { color: 'hsl(var(--foreground))', fontWeight: 600 },
    selector: { color: 'hsl(var(--foreground))', fontWeight: 600 },
    'attr-name': { color: 'hsl(var(--muted-foreground))' },
    string: { color: 'hsl(var(--muted-foreground))' },
    char: { color: 'hsl(var(--muted-foreground))' },
    'attr-value': { color: 'hsl(var(--muted-foreground))' },
    url: { color: 'hsl(var(--muted-foreground))' },
    regex: { color: 'hsl(var(--muted-foreground))' },
    function: { color: 'hsl(var(--foreground))', fontWeight: 500 },
    'class-name': { color: 'hsl(var(--foreground))', fontWeight: 500 },
    tag: { color: 'hsl(var(--foreground))', fontWeight: 500 },
    number: { color: 'hsl(var(--foreground))' },
    boolean: { color: 'hsl(var(--foreground))' },
    constant: { color: 'hsl(var(--foreground))' },
    symbol: { color: 'hsl(var(--foreground))' },
    property: { color: 'hsl(var(--foreground))' },
    builtin: { color: 'hsl(var(--foreground))' },
    variable: { color: 'hsl(var(--foreground))' },
    inserted: { color: 'hsl(var(--ok))' },
    deleted: { color: 'hsl(var(--err))' },
    bold: { fontWeight: 600 },
    italic: { fontStyle: 'italic' },
};

export const AICodeBlock = memo(({
    code,
    language = 'typescript',
    filename,
    showLineNumbers = true,
    maxHeight = '400px',
    className
}: AICodeBlockProps) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const lineCount = code.trim().split('\n').length;

    return (
        <div
            className={cn(
                "border border-border overflow-hidden bg-surface-3",
                className
            )}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-card">
                <div className="flex items-center gap-2 min-w-0">
                    {filename ? (
                        <span className="truncate font-mono text-[11px] text-muted-foreground">{filename}</span>
                    ) : (
                        <span className="font-mono text-[10px] uppercase tracking-wider text-faint">
                            {languageNames[language] || language.toUpperCase()}
                        </span>
                    )}
                    <span className="shrink-0 font-mono text-[11px] text-faint">
                        {lineCount} ligne{lineCount > 1 ? 's' : ''}
                    </span>
                </div>

                <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-2 py-1 text-xs text-faint hover:bg-secondary hover:text-foreground transition-colors"
                >
                    {copied ? (
                        <>
                            <Check className="h-3 w-3 text-ok" />
                            <span className="text-ok">Copié</span>
                        </>
                    ) : (
                        <>
                            <Copy className="h-3 w-3" />
                            <span>Copier</span>
                        </>
                    )}
                </button>
            </div>

            {/* Code */}
            <div
                className="overflow-auto font-mono text-[13px] leading-normal"
                style={{ maxHeight }}
            >
                <SyntaxHighlighter
                    language={language}
                    style={offsetMonochrome}
                    showLineNumbers={showLineNumbers}
                    customStyle={{
                        margin: 0,
                        padding: '12px',
                        background: 'transparent',
                        fontSize: '13px',
                    }}
                    codeTagProps={{
                        style: { fontFamily: 'inherit' }
                    }}
                    lineNumberStyle={{
                        minWidth: '2.5em',
                        paddingRight: '1em',
                        color: 'hsl(var(--faint) / 0.6)',
                        textAlign: 'right',
                    }}
                >
                    {code.trim()}
                </SyntaxHighlighter>
            </div>
        </div>
    );
});

AICodeBlock.displayName = 'AICodeBlock';

// Inline code component
export interface AIInlineCodeProps {
    children: React.ReactNode;
    className?: string;
}

export const AIInlineCode = memo(({
    children,
    className
}: AIInlineCodeProps) => {
    return (
        <code
            className={cn(
                "px-1.5 py-0.5 bg-secondary font-mono text-[13px] text-foreground",
                className
            )}
        >
            {children}
        </code>
    );
});

AIInlineCode.displayName = 'AIInlineCode';

export default AICodeBlock;
